import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Table,
  Button, Spinner, Alert,
} from 'react-bootstrap';
import Layout from '../components/Layout';
import usersService from '../services/usersService';
import qnaService from '../services/qnaService';

const STATUS_LABELS = {
  '':         { label: 'جديد',         variant: 'secondary' },
  pending:    { label: 'قيد الانتظار', variant: 'warning'   },
  answered:   { label: 'تم الجواب',    variant: 'info'      },
  replied:    { label: 'تم الرد',      variant: 'success'   },
  trash:      { label: 'مهمل',         variant: 'danger'    },
};

// Legacy values still present in the sheet, folded onto the current workflow.
const STATUS_ALIASES = { 'مفتوح': '', audited: 'answered' };

const normalizeStatus = (value) => {
  const status = value || '';
  return status in STATUS_ALIASES ? STATUS_ALIASES[status] : status;
};

const fmtDate = (v) => {
  if (!v) return '-';
  try {
    return new Date(v).toLocaleString('ar-IQ', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return v;
  }
};

const InfoRow = ({ icon, label, value }) => (
  <div className="d-flex align-items-start gap-2 mb-2">
    <i className={`${icon} text-secondary mt-1`} style={{ flexShrink: 0, fontSize: '0.9rem' }} />
    <div>
      <div className="text-muted" style={{ fontSize: '0.72rem' }}>{label}</div>
      <div style={{ fontSize: '0.88rem', wordBreak: 'break-all' }}>{value || '-'}</div>
    </div>
  </div>
);

export default function UserQuestions() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [users, qna] = await Promise.all([
        usersService.getAll(),
        qnaService.getAll(),
      ]);
      const found = (users || []).find(u => u.uid === uid) || null;
      setUser(found);
      setQuestions(qna.questions || []);
    } catch (e) {
      setError('تعذّر تحميل البيانات: ' + (e.message || 'خطأ غير معروف'));
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  const userQuestions = useMemo(() => {
    const email = user?.email?.trim().toLowerCase();
    if (!email) return [];
    return questions
      .filter(q => q.email?.trim().toLowerCase() === email)
      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  }, [questions, user]);

  const breadcrumbs = [
    { label: 'الرئيسية', path: '/dashboard', icon: 'bi-house' },
    { label: 'المستخدمون', path: '/users', icon: 'bi-people-fill' },
    { label: user?.fullName || 'أسئلة المستخدم', icon: 'bi-chat-dots' },
  ];

  return (
    <Layout title="أسئلة المستخدم" breadcrumbs={breadcrumbs}>
      <Container fluid dir="rtl">

        <Button variant="outline-secondary" size="sm" className="mb-3" onClick={() => navigate('/users')}>
          <i className="bi bi-arrow-right ms-1" />
          رجوع إلى المستخدمين
        </Button>

        {error && (
          <Alert variant="danger" className="d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-triangle-fill" />
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="text-muted mt-3">جارٍ تحميل البيانات...</div>
          </div>
        ) : !user ? (
          <Alert variant="warning" className="d-flex align-items-center gap-2">
            <i className="bi bi-person-x-fill" />
            لم يُعثر على المستخدم
          </Alert>
        ) : (
          <>
            {/* User summary */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="fw-semibold text-secondary small mb-3">
                      <i className="bi bi-person-lines-fill ms-1" />
                      بيانات المستخدم
                    </div>
                    <InfoRow icon="bi-person" label="الاسم" value={user.fullName} />
                    <InfoRow icon="bi-envelope" label="البريد الإلكتروني" value={user.email} />
                    <InfoRow icon="bi-whatsapp" label="واتساب" value={user.whatsapp} />
                  </Col>
                  <Col md={6}>
                    <div className="fw-semibold text-secondary small mb-3">
                      <i className="bi bi-graph-up ms-1" />
                      ملخص
                    </div>
                    <InfoRow icon="bi-chat-dots" label="عدد الأسئلة" value={userQuestions.length} />
                    <InfoRow
                      icon="bi-check-circle"
                      label="تم الرد عليها"
                      value={userQuestions.filter(q => q.answer?.trim()).length}
                    />
                    <InfoRow icon="bi-globe" label="المنصة" value={user.platform} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Questions */}
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle" style={{ fontSize: '0.9rem' }}>
                    <thead className="table-light">
                      <tr>
                        <th className="d-none d-md-table-cell" style={{ width: 45 }}>#</th>
                        <th className="d-none d-md-table-cell" style={{ width: 120 }}>التاريخ</th>
                        <th style={{ width: 110 }}>رقم التذكرة</th>
                        <th>السؤال</th>
                        <th className="d-none d-md-table-cell">الكتاب</th>
                        <th style={{ width: 120 }}>الحالة</th>
                        <th style={{ width: 60 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {userQuestions.length === 0 && (
                        <tr>
                          <td colSpan={7} className="text-center py-5 text-muted">
                            <i className="bi bi-inbox fs-3 d-block mb-2" />
                            لا توجد أسئلة لهذا المستخدم
                          </td>
                        </tr>
                      )}
                      {userQuestions.map((q, idx) => {
                        const isExpanded = expanded === q.id;
                        const status = STATUS_LABELS[normalizeStatus(q.status)] || STATUS_LABELS[''];
                        return (
                          <React.Fragment key={q.id}>
                            <tr
                              onClick={() => setExpanded(isExpanded ? null : q.id)}
                              style={{
                                cursor: 'pointer',
                                background: isExpanded ? '#f0f4ff' : undefined,
                                borderRight: isExpanded ? '3px solid #0d6efd' : '3px solid transparent',
                              }}
                            >
                              <td className="d-none d-md-table-cell text-muted">{idx + 1}</td>
                              <td className="d-none d-md-table-cell text-muted" style={{ fontSize: '0.8rem' }}>
                                {fmtDate(q.timestamp)}
                              </td>
                              <td className="text-muted" style={{ fontSize: '0.85rem' }}>
                                {q.ticketNumber || '-'}
                              </td>
                              <td style={{ maxWidth: 320 }}>
                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {q.question}
                                </div>
                              </td>
                              <td className="d-none d-md-table-cell text-muted" style={{ fontSize: '0.85rem' }}>
                                {q.book || '-'}
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-1">
                                  <span className={`badge text-bg-${status.variant}`} style={{ fontSize: '0.78rem' }}>
                                    {status.label}
                                  </span>
                                  {q.audit && (
                                    <span
                                      className="badge"
                                      style={{
                                        fontSize: '0.78rem',
                                        background: 'rgba(249,168,37,0.12)',
                                        color: '#F9A825',
                                        border: '1px solid rgba(249,168,37,0.4)',
                                      }}
                                    >
                                      <i className="bi bi-patch-check-fill ms-1" />
                                      معتمد
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td onClick={e => e.stopPropagation()}>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  title={isExpanded ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}
                                  onClick={() => setExpanded(isExpanded ? null : q.id)}
                                >
                                  <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                                </Button>
                              </td>
                            </tr>

                            {isExpanded && (
                              <tr style={{ background: '#f8f9ff' }}>
                                <td colSpan={7} className="p-3 pt-2">
                                  <div className="mb-3">
                                    <div className="text-secondary fw-semibold small mb-1">
                                      <i className="bi bi-question-circle ms-1" />
                                      السؤال:
                                    </div>
                                    <div
                                      style={{
                                        background: '#fff',
                                        border: '1px solid #dee2e6',
                                        borderRadius: 8,
                                        padding: '10px 14px',
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.9,
                                        fontSize: '0.92rem',
                                      }}
                                    >
                                      {q.question}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-secondary fw-semibold small mb-1">
                                      <i className="bi bi-chat-left-text ms-1" />
                                      الجواب:
                                    </div>
                                    <div
                                      style={{
                                        background: q.answer ? '#f0faf0' : '#fff',
                                        border: '1px solid #dee2e6',
                                        borderRadius: 8,
                                        padding: '10px 14px',
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.9,
                                        fontSize: '0.92rem',
                                        color: q.answer ? undefined : '#6c757d',
                                      }}
                                    >
                                      {q.answer || 'لم يتم الرد بعد'}
                                    </div>
                                    {q.sheikh && (
                                      <div className="text-muted small mt-2">
                                        <i className="bi bi-person-badge ms-1" />
                                        الشيخ المعيّن: {q.sheikh}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
    </Layout>
  );
}
